import React, { useCallback } from 'react';
import Styled from './styled';
import {
    Alert,
    Button,
    CloseIcon,
    LoadingIndicator,
    RingIconOld,
} from '../index';
import { useTipTapViewer } from '../../utils/TipTap';
import { EditorContent } from '@tiptap/react';


export type WelcomeScreenProps = {
    /** accessibility support (alt text/aria-label, defaults to 'minimize') */
    ariaLabel?: string;
    /** title text for the WelcomeScreen component */
    title: string;
    /** subtitle, terms and conditions */
    subtitleHtml: string;
    /** Label for the start chat button */
    buttonText: string;
    /** Children content to display inside the body. */
    children?: React.ReactNode;
    /** Handler function to start the chat */
    startHandler: () => void;
    /** Click handler to toggle widget */
    toggleOpen: () => void;
    /** Loading state for startHandler */
    loading?: boolean;
    /** Error verbiage associated with startHandler or issue with Credentials passed */
    error?: string;
    /** Accessibility Label for the welcome message. (defaults to 'welcome text') */
    messageLabel?: string;
};

/**
 * WelcomeScreen component that displays a title, subtitle, and a button to start the chat.
 * It replaces the body of the chat widget when the user has not provided credentials.
 * 
 * @param {WelcomeScreenProps} props - The properties to configure the WelcomeScreen component.
 * @returns {JSX.Element} The rendered WelcomeScreen component.
 */
export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({
    ariaLabel = 'minimize',
    title,
    subtitleHtml,
    buttonText,
    children,
    startHandler,
    toggleOpen,
    loading,
    error,
    messageLabel = 'welcome text',
}) => {

    const viewer = useTipTapViewer({
        message: subtitleHtml,
        textOnly: false,
        ariaLabel: messageLabel,
    });

    return (
        <Styled.Widget>
            {/* Match UI of Live Chat, Close Icon in header (upper right corner) */}
            <Styled.CloseIcon
                onClick={toggleOpen}
                aria-label={ariaLabel}
                tabIndex={0}
                role='button'
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault(); // Prevent default scrolling for Space key
                        toggleOpen();
                    }
                }}
            >
                <CloseIcon />
            </Styled.CloseIcon>
            {error &&
                <Alert
                    color='negative'
                    message={error}
                    position='topBody'
                />
            }
            <Styled.ContentWrapper>
                <Styled.IconWrapper>
                    <Styled.Icon><RingIconOld /></Styled.Icon>
                </Styled.IconWrapper>
                <Styled.Title>{title}</Styled.Title>
                <Styled.Subtitle>
                    <EditorContent editor={viewer} />
                </Styled.Subtitle>
                {loading ?
                    <LoadingIndicator height='50px' width='50px' /> :
                    <Styled.StartButton>
                        <Button
                            label={buttonText}
                            value='welcome'
                            onClick={startHandler}
                            disabled={loading}
                        />
                    </Styled.StartButton>
                }
            </Styled.ContentWrapper>
            {children}
        </Styled.Widget>
    );
};
